import { db as firebaseDb } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { AssetInstallation, RemovalReason } from '@easydone/shared';

/**
 * Commission an asset into a FLOC
 */
export async function commissionAsset(
  orgId: string,
  data: {
    assetId: string;
    flocId: string;
    conditionAtInstall?: number;
    installationNotes?: string;
  },
  userId: string,
  userName: string
): Promise<AssetInstallation> {
  const installationsRef = collection(
    firebaseDb,
    'organizations',
    orgId,
    'assetInstallations'
  );

  const installationData = {
    orgId,
    assetId: data.assetId,
    flocId: data.flocId,
    installedAt: serverTimestamp(),
    installedBy: userId,
    installedByName: userName,
    installationNotes: data.installationNotes || null,
    removedAt: null,
    removedBy: null,
    removedByName: null,
    removalReason: null,
    removalNotes: null,
    conditionAtInstall: data.conditionAtInstall || null,
    conditionAtRemoval: null,
    metadata: {},
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(installationsRef, installationData);

  // Update asset
  const assetRef = doc(firebaseDb, 'organizations', orgId, 'assets', data.assetId);
  await updateDoc(assetRef, {
    lifecycleStatus: 'commissioned',
    currentFlocId: data.flocId,
    storageLocation: null,
    commissionedDate: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Update FLOC
  const flocRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', data.flocId);
  await updateDoc(flocRef, {
    installedAssetId: data.assetId,
    operationalStatus: 'operational',
    updatedAt: serverTimestamp(),
  });

  const snapshot = await getDoc(docRef);
  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as AssetInstallation;
}

/**
 * Decommission an asset from a FLOC
 */
export async function decommissionAsset(
  orgId: string,
  assetId: string,
  data: {
    removalReason: RemovalReason;
    removalNotes?: string;
    conditionAtRemoval?: number;
    storageLocation?: string;
  },
  userId: string,
  userName: string
): Promise<void> {
  // Find active installation
  const installationsRef = collection(
    firebaseDb,
    'organizations',
    orgId,
    'assetInstallations'
  );
  const q = query(
    installationsRef,
    where('assetId', '==', assetId),
    where('removedAt', '==', null),
    limit(1)
  );
  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    throw new Error('No active installation found for this asset');
  }

  const installation = snapshot.docs[0];
  const installationData = installation.data();

  // Calculate days installed
  const installedAt = installationData.installedAt.toDate();
  const now = new Date();
  const daysInstalled = Math.floor((now.getTime() - installedAt.getTime()) / (1000 * 60 * 60 * 24));

  // Update installation record
  await updateDoc(installation.ref, {
    removedAt: serverTimestamp(),
    removedBy: userId,
    removedByName: userName,
    removalReason: data.removalReason,
    removalNotes: data.removalNotes || null,
    conditionAtRemoval: data.conditionAtRemoval || null,
    daysInstalled,
    updatedAt: serverTimestamp(),
  });

  // Update asset
  const assetRef = doc(firebaseDb, 'organizations', orgId, 'assets', assetId);
  const newLifecycleStatus =
    data.removalReason === 'maintenance' ? 'in_maintenance' :
    data.removalReason === 'decommission' ? 'decommissioned' : 'in_storage';

  await updateDoc(assetRef, {
    lifecycleStatus: newLifecycleStatus,
    currentFlocId: null,
    storageLocation: data.storageLocation || null,
    decommissionedDate: data.removalReason === 'decommission' ? serverTimestamp() : null,
    updatedAt: serverTimestamp(),
  });

  // Update FLOC
  const flocRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', installationData.flocId);
  await updateDoc(flocRef, {
    installedAssetId: null,
    operationalStatus: 'vacant',
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get installation history for an asset
 */
export async function getAssetInstallationHistory(
  orgId: string,
  assetId: string
): Promise<AssetInstallation[]> {
  const installationsRef = collection(
    firebaseDb,
    'organizations',
    orgId,
    'assetInstallations'
  );
  const q = query(
    installationsRef,
    where('assetId', '==', assetId),
    orderBy('installedAt', 'desc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as AssetInstallation));
}

/**
 * Get installation history for a FLOC
 */
export async function getFlocInstallationHistory(
  orgId: string,
  flocId: string
): Promise<AssetInstallation[]> {
  const installationsRef = collection(
    firebaseDb,
    'organizations',
    orgId,
    'assetInstallations'
  );
  const q = query(
    installationsRef,
    where('flocId', '==', flocId),
    orderBy('installedAt', 'desc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as AssetInstallation));
}

/**
 * Get current installation for an asset
 */
export async function getCurrentInstallation(
  orgId: string,
  assetId: string
): Promise<AssetInstallation | null> {
  const installationsRef = collection(
    firebaseDb,
    'organizations',
    orgId,
    'assetInstallations'
  );
  const q = query(
    installationsRef,
    where('assetId', '==', assetId),
    where('removedAt', '==', null),
    limit(1)
  );
  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const doc = snapshot.docs[0];
  return {
    id: doc.id,
    ...doc.data(),
  } as AssetInstallation;
}
